import React, { useRef } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import useChangelogMedia from '../hooks/useChangelogMedia';
import { resolveChangelogMediaUrl } from '../../../../api/PublicChangelogAPI';

const ACCEPT = {
  image: 'image/png,image/jpeg,image/webp,image/gif',
  poster: 'image/png,image/jpeg,image/webp',
  video: 'video/mp4,video/webm',
  captions: '.vtt,text/vtt',
};

const mediaName = (item) => item.label || item.original_name || `Arquivo ${item.id}`;

/**
 * Escolhe uma mídia já enviada de um tipo ou sobe uma nova (que já fica escolhida).
 * Imagem e capa mostram a miniatura da escolhida.
 *
 * @param {{ kind: 'image'|'video'|'poster'|'captions', label: string, value: number|null,
 *   onChange: Function, uploadLabel?: string, disabled?: boolean }} props
 */
function MediaPicker({ kind, label, value, onChange, uploadLabel, disabled = false }) {
  const { media, isLoading, isUploading, error, upload } = useChangelogMedia(kind);
  const inputRef = useRef(null);
  const selected = media.find((item) => item.id === value) || null;
  const showThumb = (kind === 'image' || kind === 'poster') && selected?.url;
  // Enquanto a lista carrega, o valor atual ainda não está entre as opções
  const selectValue = selected ? selected.id : '';

  const handleFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    upload(file, uploadLabel || file.name, (created) => onChange(created.id));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <FormControl size="small" fullWidth disabled={disabled || isLoading}>
          <InputLabel>{label}</InputLabel>
          <Select
            label={label}
            value={selectValue}
            onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
          >
            <MenuItem value=""><em>Nenhum</em></MenuItem>
            {media.map((item) => (
              <MenuItem key={item.id} value={item.id}>{mediaName(item)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          size="small"
          startIcon={<UploadFileOutlinedIcon />}
          onClick={() => inputRef.current?.click()}
          disabled={disabled || isUploading}
          sx={{ whiteSpace: 'nowrap', flexShrink: 0, height: 40 }}
        >
          {isUploading ? 'Enviando...' : 'Enviar'}
        </Button>
        <input ref={inputRef} type="file" accept={ACCEPT[kind]} hidden onChange={handleFile} />
      </Box>
      {showThumb && (
        <Box
          component="img"
          src={resolveChangelogMediaUrl(selected.url)}
          alt={mediaName(selected)}
          sx={{ mt: 1, maxWidth: '100%', maxHeight: 140, borderRadius: '8px', border: '1px solid #E2E8F0', display: 'block' }}
        />
      )}
      {error && <Typography variant="caption" color="error" component="div" sx={{ mt: 0.5 }}>{error}</Typography>}
    </Box>
  );
}

export default MediaPicker;
